import {
    FiasAddressFields,
    FiasExtendFieldsType,
    FiasFlatRoom,
    FiasHouseGar,
    ParsedExtendedFiasField,
    SuggestAddress,
} from "../Types/FiasTypes";
import { splitArrayByPredicate } from "../../../../../../../Common/ArrayUtils";

import { getHouseTypeShortName } from "./Convertations/House/House";
import { getFlatTypeShortName } from "./Convertations/Flat/Flat";

export const extractUniqAddresses = (addresses: FiasAddressFields[]): FiasAddressFields[] => {
    const result: string[] = [];
    const uniq: FiasAddressFields[] = [];

    for (const address of addresses) {
        if (address.suggest?.fiasId) {
            if (!result.includes(address.suggest.fiasId)) {
                result.push(address.suggest.fiasId);
                uniq.push(address);
            }
        }
    }

    if (result.length === addresses.length) {
        return addresses;
    }
    return uniq;
};

export const getHouseFiasIdFromAddress = (addresses: FiasAddressFields[]): string | undefined => {
    const firstSuggestAddress = addresses[0];
    if (firstSuggestAddress) {
        const levels = Object.keys(firstSuggestAddress).filter(level => level !== "suggest");
        const lastLevel = levels.pop();
        if (lastLevel === "garhouse" && firstSuggestAddress.street) {
            return firstSuggestAddress.street?.fiasId;
        } else {
            return undefined;
        }
    }
    return undefined;
};

export const parseFullAddressFromRequest = (structuredAddress: FiasAddressFields): string => {
    const fullStringAddress: string[] = [];

    const levels = Object.keys(structuredAddress);
    for (const level of levels) {
        // @ts-ignore
        const levelPoint = structuredAddress[level];
        if (level === "region") {
            fullStringAddress.push(`${levelPoint.name} ${levelPoint.abbreviation}`);
        } else if (levelPoint.houseType && levelPoint.houseNum) {
            const houseGar: FiasHouseGar = levelPoint;
            fullStringAddress.push(parseHouseLeverToText(houseGar));
        } else if (levelPoint.flatType) {
            const flat: FiasFlatRoom = levelPoint;
            fullStringAddress.push(`${getFlatTypeShortName(flat.flatType)} ${flat.flatNumber}`);
            if (levelPoint.roomNumber) {
                const room: FiasFlatRoom = levelPoint;
                fullStringAddress.push(`${getFlatTypeShortName(room.roomType)} ${levelPoint.roomNumber}`);
            }
        } else if (level === "stead") {
            fullStringAddress.push(`з/у ${levelPoint.number}`);
        } else {
            fullStringAddress.push(`${levelPoint.abbreviation} ${levelPoint.name}`);
        }
    }
    return fullStringAddress.join(", ");
};

const parseHouseLeverToText = (houseLevel: FiasHouseGar): string => {
    const houseLevelText: string[] = [];
    if (houseLevel.houseNum && houseLevel.houseType) {
        houseLevelText.push(`${getHouseTypeShortName(houseLevel.houseType)} ${houseLevel.houseNum}`);
    }
    if (houseLevel.addNum1 && houseLevel.addType1) {
        houseLevelText.push(`${getHouseTypeShortName(houseLevel.addType1)} ${houseLevel.addNum1}`);
    }
    if (houseLevel.addNum2 && houseLevel.addType2) {
        houseLevelText.push(`${getHouseTypeShortName(houseLevel.addType2)} ${houseLevel.addNum2}`);
    }
    return houseLevelText.join(" ");
};

export const getItemAddressText = (suggestAddress: FiasAddressFields): { longText: string; shortText: string } => {
    const addressLevelWithoutSuggestAndLastLevel: FiasAddressFields = {};
    const parseSuggestAddress = Object.keys(suggestAddress) as Array<keyof FiasAddressFields>;
    parseSuggestAddress.shift();

    let levelsCounter = -1;
    let shortText = "";
    for (const [key, addr] of Object.entries(suggestAddress)) {
        levelsCounter++;
        if (levelsCounter === 0) {
            continue;
        } else if (levelsCounter === parseSuggestAddress.length) {
            shortText = parseFullAddressFromRequest({ [key]: addr });
            continue;
        }
        // @ts-ignore
        addressLevelWithoutSuggestAndLastLevel[key] = addr;
    }
    const longText = parseFullAddressFromRequest(addressLevelWithoutSuggestAndLastLevel);

    return { longText: longText, shortText: shortText };
};

export function extractSuggestFields(address: SuggestAddress | undefined): SuggestAddress {
    return {
        value: address?.value ?? "",
        fiasId: address?.fiasId ?? "",
        level: address?.level ?? "",
        label: address?.label ?? "",
        actualLevel: address?.actualLevel ?? "",
        fiasLevel: address?.fiasLevel ?? "",
        matchResult: address?.matchResult ?? "",
    };
}

interface NecessarilySplittedExtendedFields {
    notRequiredExtendedFields: ParsedExtendedFiasField[];
    requiredExtendedFields: ParsedExtendedFiasField[];
}

export const splitExtendedFieldsForStringAddress = (
    extendedFields: ParsedExtendedFiasField[]
): NecessarilySplittedExtendedFields => {
    const [requiredExtendedFields, notRequiredExtendedFields] = splitArrayByPredicate(
        extendedFields,
        item => item.type !== FiasExtendFieldsType.guid
    );

    return {
        requiredExtendedFields: requiredExtendedFields,
        notRequiredExtendedFields: notRequiredExtendedFields,
    };
};
